import { Skeleton } from "@loot-os/ui/components/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { centsToDollars, dashIfEmpty } from "../lib/format";
import { useTRPC } from "../lib/trpc";
import { ErrorAlert } from "./ErrorAlert";

/**
 * The rows a promote would write from the cached fetch, with the sizes each keeps and their
 * costs, read off the server's preview so the list matches what promote writes. The tags set
 * the markup, so the query reruns as they change, and so does the size band choice; the last
 * answer stays on screen meanwhile.
 */
export function PromotionPreview({
  vendorProductKey,
  tags,
  withSizeBands,
}: {
  vendorProductKey: string;
  tags: string[];
  /** Whether the preview also lists the size band rows the promote would write. */
  withSizeBands: boolean;
}) {
  const trpc = useTRPC();
  const preview = useQuery(
    trpc.platform.fulfillment.productFetch.preview.queryOptions(
      { vendorProductKey, tags, withSizeBands },
      { placeholderData: keepPreviousData, retry: false },
    ),
  );

  if (preview.error) return <ErrorAlert message={preview.error.message} />;
  if (!preview.data) return <Skeleton className="h-24 w-full" />;
  // The server adds SNEAKERS to every row of a StockX sneaker, so name it when the picker lacks it.
  const addedTags = [...new Set(preview.data.rows.flatMap((row) => row.tags))].filter(
    (tag) => !tags.includes(tag),
  );

  return (
    <div className="grid gap-3">
      {addedTags.length > 0 && (
        <p className="text-xs text-muted-foreground">
          StockX lists this product as a sneaker, so every row also gets{" "}
          <code>{addedTags.join(", ")}</code>.
        </p>
      )}
      {preview.data.rows.map((row) => (
        <div key={row.id} className="grid gap-1">
          <p className="text-sm">
            <code>{row.id}</code> at {centsToDollars(row.priceCents)}, {row.variants.length}{" "}
            {row.variants.length === 1 ? "size" : "sizes"} kept
          </p>
          <div className="max-h-48 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Size</TableHead>
                  <TableHead>Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {row.variants.map((variant) => (
                  <TableRow key={variant.variantId}>
                    <TableCell>{dashIfEmpty(variant.variantValue)}</TableCell>
                    <TableCell>{centsToDollars(variant.costCents)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ))}
    </div>
  );
}
